import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import {
  Button,
  ConfirmDialog,
  ErrorNote,
  Field,
  Loading,
  inputClass,
} from '../../components/ui';
import { errorMessage } from '../../lib/api';
import { useAuth } from '../../lib/auth-context-value';
import { apiAssetUrl, company as companyApi } from '../../lib/resources';
import type { Company, PhoneSettings, TenantSegment } from '../../types';

const SEGMENTS: Array<[TenantSegment, string]> = [
  ['OTHER', 'Outros'],
  ['ECOMMERCE', 'E-commerce'],
  ['INFOPRODUCT', 'Infoproduto'],
  ['SALES', 'Comércio de Vendas'],
  ['CLINIC', 'Clínicas'],
  ['LAW_FIRM', 'Escritório de Advogados'],
  ['AGENCY', 'Agências'],
];

/** Fusos mais usados no Brasil primeiro; o resto vem do próprio navegador. */
const TIMEZONES = (() => {
  const preferred = [
    'America/Sao_Paulo',
    'America/Fortaleza',
    'America/Recife',
    'America/Bahia',
    'America/Belem',
    'America/Manaus',
    'America/Cuiaba',
    'America/Porto_Velho',
    'America/Rio_Branco',
    'America/Noronha',
  ];
  const all =
    typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
  return [...preferred, ...all.filter((zone) => !preferred.includes(zone))];
})();

export function TimezoneSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <select
      className={inputClass}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      {TIMEZONES.map((zone) => (
        <option key={zone} value={zone}>
          {zone.replace(/_/g, ' ')}
        </option>
      ))}
    </select>
  );
}

function Block({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5">
      <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
      {description && <p className="mt-0.5 text-xs text-slate-500">{description}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

/** Cada bloco salva sozinho, como na tela do DataCrazy. */
function SaveRow({
  saving,
  error,
  saved,
}: {
  saving: boolean;
  error: string | null;
  saved: boolean;
}) {
  return (
    <div className="mt-4 flex items-center gap-3">
      <Button type="submit" variant="primary" disabled={saving}>
        {saving ? 'Salvando…' : 'Salvar'}
      </Button>
      {saved && !error && <span className="text-xs text-emerald-600">Salvo.</span>}
      {error && <ErrorNote>{error}</ErrorNote>}
    </div>
  );
}

/** Configurações › Empresa — dados do workspace (o tenant de tudo). */
export function CompanySection() {
  const query = useQuery({ queryKey: ['company'], queryFn: companyApi.get });
  if (query.isLoading || !query.data) return <Loading />;
  // `key` reinicia os formulários quando o workspace muda.
  return <CompanyForms key={query.data.id} company={query.data} />;
}

function useSave<T>(save: (payload: T) => Promise<Company>) {
  const queryClient = useQueryClient();
  const { refreshSession } = useAuth();
  const [saved, setSaved] = useState(false);
  const mutation = useMutation({
    mutationFn: save,
    onMutate: () => setSaved(false),
    onSuccess: async (next) => {
      queryClient.setQueryData(['company'], next);
      setSaved(true);
      // Nome e logo aparecem no menu lateral e no seletor de workspace.
      await refreshSession();
    },
  });
  return {
    mutate: mutation.mutate,
    saving: mutation.isPending,
    saved,
    error: mutation.error ? errorMessage(mutation.error, 'Não foi possível salvar') : null,
  };
}

function CompanyForms({ company }: { company: Company }) {
  return (
    <div className="space-y-5">
      <CompanyHeader company={company} />
      <InfoBlock company={company} />
      <LogoBlock company={company} />
      <AddressBlock company={company} />
      <PhoneBlock company={company} />
    </div>
  );
}

function CompanyHeader({ company }: { company: Company }) {
  const logo = apiAssetUrl(company.logoUrl);
  return (
    <div className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-5">
      {logo ? (
        <img
          src={logo}
          alt=""
          className="h-14 w-14 rounded-xl border border-slate-200 object-contain"
        />
      ) : (
        <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-slate-100 text-lg font-semibold text-slate-500">
          {company.name.slice(0, 1).toUpperCase()}
        </div>
      )}
      <div>
        <p className="text-base font-semibold text-slate-900">
          {company.legalName || company.name}
        </p>
        <p className="text-xs text-slate-500">
          {[
            company.email,
            SEGMENTS.find(([key]) => key === company.segment)?.[1],
            company.address.city,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
        <p className="text-xs text-slate-400">
          Criada em {new Date(company.createdAt).toLocaleDateString('pt-BR')}
        </p>
      </div>
    </div>
  );
}

function InfoBlock({ company }: { company: Company }) {
  const [form, setForm] = useState({
    name: company.name,
    legalName: company.legalName ?? '',
    phone: company.phone ?? '',
    email: company.email ?? '',
    segment: company.segment,
    segmentDescription: company.segmentDescription ?? '',
    timezone: company.timezone,
  });
  const save = useSave(companyApi.update);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate({
      name: form.name,
      legalName: form.legalName || null,
      phone: form.phone || null,
      email: form.email || null,
      segment: form.segment,
      segmentDescription: form.segmentDescription || null,
      timezone: form.timezone,
    });
  };

  return (
    <Block title="Informações" description="O fuso da empresa vale para relatórios e horários.">
      <form onSubmit={submit}>
        <div className="grid gap-3 md:grid-cols-2">
          <Field label="Nome do workspace *" hint="Aparece no seletor do topo do menu.">
            <input
              required
              className={inputClass}
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          </Field>
          <Field label="Razão social">
            <input
              className={inputClass}
              value={form.legalName}
              onChange={(event) => setForm({ ...form, legalName: event.target.value })}
            />
          </Field>
          <Field label="Telefone">
            <input
              className={inputClass}
              placeholder="+55 85 99999-0000"
              value={form.phone}
              onChange={(event) => setForm({ ...form, phone: event.target.value })}
            />
          </Field>
          <Field label="E-mail da empresa">
            <input
              type="email"
              className={inputClass}
              value={form.email}
              onChange={(event) => setForm({ ...form, email: event.target.value })}
            />
          </Field>
          <Field label="Segmento">
            <select
              className={inputClass}
              value={form.segment}
              onChange={(event) =>
                setForm({ ...form, segment: event.target.value as TenantSegment })
              }
            >
              {SEGMENTS.map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Qual é o seu segmento?">
            <input
              className={inputClass}
              placeholder="Exemplo: Consultoria financeira"
              value={form.segmentDescription}
              onChange={(event) => setForm({ ...form, segmentDescription: event.target.value })}
            />
          </Field>
          <Field label="Fuso horário da empresa">
            <TimezoneSelect
              value={form.timezone}
              onChange={(timezone) => setForm({ ...form, timezone })}
            />
          </Field>
        </div>
        <SaveRow {...save} />
      </form>
    </Block>
  );
}

function LogoBlock({ company }: { company: Company }) {
  const queryClient = useQueryClient();
  const { refreshSession } = useAuth();
  const input = useRef<HTMLInputElement>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const onDone = async (next: Company) => {
    queryClient.setQueryData(['company'], next);
    setConfirmRemove(false);
    await refreshSession();
  };
  const upload = useMutation({ mutationFn: companyApi.uploadLogo, onSuccess: onDone });
  const remove = useMutation({ mutationFn: companyApi.removeLogo, onSuccess: onDone });
  const error = upload.error ?? remove.error;

  return (
    <Block
      title="Logo da empresa"
      description="PNG, JPG, WEBP ou SVG, até 2 MB. Aparece no menu lateral."
    >
      <div className="flex flex-wrap items-center gap-3">
        <input
          ref={input}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) upload.mutate(file);
            event.target.value = '';
          }}
        />
        <Button onClick={() => input.current?.click()} disabled={upload.isPending}>
          {upload.isPending ? 'Enviando…' : 'Escolher arquivo'}
        </Button>
        {company.logoUrl && (
          <Button variant="ghost" onClick={() => setConfirmRemove(true)}>
            Remover logo
          </Button>
        )}
        {error && (
          <ErrorNote>{errorMessage(error, 'Não foi possível salvar o logo')}</ErrorNote>
        )}
      </div>
      {confirmRemove && (
        <ConfirmDialog
          title="Remover o logo?"
          message="O menu lateral volta a mostrar só o nome do workspace."
          confirmLabel="Remover"
          danger
          busy={remove.isPending}
          onConfirm={() => remove.mutate()}
          onClose={() => setConfirmRemove(false)}
        />
      )}
    </Block>
  );
}

function AddressBlock({ company }: { company: Company }) {
  const [form, setForm] = useState({
    country: company.address.country ?? 'BR',
    taxId: company.taxId ?? '',
    zip: company.address.zip ?? '',
    address: company.address.address ?? '',
    number: company.address.number ?? '',
    complement: company.address.complement ?? '',
    block: company.address.block ?? '',
    city: company.address.city ?? '',
    state: company.address.state ?? '',
  });
  const save = useSave(companyApi.updateAddress);
  const isBrazil = form.country.toUpperCase() === 'BR';
  const set = (key: keyof typeof form, value: string) => setForm({ ...form, [key]: value });

  return (
    <Block title="Endereço">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate({ ...form, taxId: form.taxId || null });
        }}
      >
        <div className="grid gap-3 md:grid-cols-3">
          <Field label="País" hint="Código de 2 letras (BR, PT, US…)">
            <input
              required
              maxLength={2}
              className={inputClass}
              value={form.country}
              onChange={(event) => set('country', event.target.value.toUpperCase())}
            />
          </Field>
          <Field
            label={`CNPJ ou CPF${isBrazil ? ' *' : ''}`}
            hint={isBrazil ? 'Obrigatório para empresas neste país' : undefined}
          >
            <input
              required={isBrazil}
              className={inputClass}
              value={form.taxId}
              onChange={(event) => set('taxId', event.target.value)}
            />
          </Field>
          <Field label="CEP">
            <input
              className={inputClass}
              value={form.zip}
              onChange={(event) => set('zip', event.target.value)}
            />
          </Field>
          <Field label="Endereço">
            <input
              className={inputClass}
              value={form.address}
              onChange={(event) => set('address', event.target.value)}
            />
          </Field>
          <Field label="Número">
            <input
              className={inputClass}
              value={form.number}
              onChange={(event) => set('number', event.target.value)}
            />
          </Field>
          <Field label="Complemento">
            <input
              className={inputClass}
              value={form.complement}
              onChange={(event) => set('complement', event.target.value)}
            />
          </Field>
          <Field label="Bairro">
            <input
              className={inputClass}
              value={form.block}
              onChange={(event) => set('block', event.target.value)}
            />
          </Field>
          <Field label="Cidade">
            <input
              className={inputClass}
              value={form.city}
              onChange={(event) => set('city', event.target.value)}
            />
          </Field>
          <Field label="UF">
            <input
              maxLength={2}
              className={inputClass}
              value={form.state}
              onChange={(event) => set('state', event.target.value.toUpperCase())}
            />
          </Field>
        </div>
        <SaveRow {...save} />
      </form>
    </Block>
  );
}

function PhoneBlock({ company }: { company: Company }) {
  const [form, setForm] = useState<PhoneSettings>(company.phoneSettings);
  const [allowed, setAllowed] = useState(company.phoneSettings.allowedCountries.join(', '));
  const save = useSave(companyApi.updatePhoneSettings);

  return (
    <Block
      title="Preferências de telefone"
      description="Como os telefones de leads e contatos são interpretados na criação e na importação."
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate({
            ...form,
            allowedCountries: allowed
              .split(/[,\s]+/)
              .map((code) => code.trim().toUpperCase())
              .filter((code) => code.length === 2),
          });
        }}
      >
        <div className="grid gap-3 md:grid-cols-2">
          <Field label="País padrão">
            <input
              maxLength={2}
              className={inputClass}
              value={form.defaultCountry}
              onChange={(event) =>
                setForm({ ...form, defaultCountry: event.target.value.toUpperCase() })
              }
            />
          </Field>
          <Field label="Modo">
            <select
              className={inputClass}
              value={form.mode}
              onChange={(event) =>
                setForm({ ...form, mode: event.target.value as PhoneSettings['mode'] })
              }
            >
              <option value="INTERNATIONAL">Internacional</option>
              <option value="NATIONAL">Nacional</option>
            </select>
          </Field>
          <Field
            label="Países permitidos"
            hint="Códigos separados por vírgula. Vazio = todos os países."
          >
            <input
              className={inputClass}
              placeholder="Todos os países"
              value={allowed}
              onChange={(event) => setAllowed(event.target.value)}
            />
          </Field>
          <Field label="Nível de confiança">
            <select
              className={inputClass}
              value={form.confidence}
              onChange={(event) =>
                setForm({
                  ...form,
                  confidence: event.target.value as PhoneSettings['confidence'],
                })
              }
            >
              <option value="LOW">Baixo</option>
              <option value="MEDIUM">Médio</option>
              <option value="HIGH">Alto</option>
            </select>
          </Field>
        </div>
        <SaveRow {...save} />
      </form>
    </Block>
  );
}
